import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import DropzoneArea from '../DropzoneArea';
import XtramModal, {
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSearch,
    XtramSelect,
    XtramStat,
    XtramTable,
    xtramStyles as styles,
} from '../../../Widgets/XtramModal/XtramModal';
import Constants from '../../../../assests/Constants/constants';

const CSV_ACCEPT = { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] };

const formatAmount = (amount) => {
    if (amount == null || amount === '') return '';
    return new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(amount));
};

/** The two-phase Atzmait CSV import: preview (missing sort codes and the rows), then create the codes and import. */
const AtzmaitCsvImportModal = ({
    open,
    onClose,
    file,
    onFileDrop,
    onFileReset,
    previewAtzmaitCsv,
    commitAtzmaitCsv,
    createMissingSortCodes,
    setMissingSortCodes,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    direction,
    t,
}) => {
    const [tab, setTab] = useState('file');
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [editableMissing, setEditableMissing] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkVatType, setBulkVatType] = useState(null);
    const [bulkGroup, setBulkGroup] = useState(null);
    const [bulkPar, setBulkPar] = useState(null);
    const [bulkVat, setBulkVat] = useState(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [importDuplicates, setImportDuplicates] = useState(false);

    const resetPreview = useCallback(() => {
        setPreviewData(null);
        setEditableMissing([]);
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
        setSearch('');
        setPage(0);
        setImportDuplicates(false);
        setTab('file');
    }, []);

    useEffect(() => {
        resetPreview();
    }, [file, resetPreview]);

    const participationOptions = useMemo(
        () => [{ label: t('Yes'), value: true }, { label: t('No'), value: false }],
        [t]
    );
    const vatPercentageOptions = useMemo(
        () => (Constants.VATPercentage || []).map((v) => ({ label: `${v}%`, value: v })),
        []
    );

    const missingIncomplete = useMemo(
        () => editableMissing.some((sc) =>
            !sc.sortCodeName?.trim() ||
            sc.sortCodeVatType == null ||
            sc.participatesInProfitAndLoss == null ||
            sc.vat == null),
        [editableMissing]
    );

    const entries = useMemo(() => previewData?.entries ?? [], [previewData]);

    const summaries = useMemo(() => ({
        sumAmount: entries.reduce((s, e) => s + (Number(e.amount) || 0), 0),
        sumNet: entries.reduce((s, e) => s + (Number(e.originalAmount) || 0), 0),
        sumVat: entries.reduce((s, e) => s + (Number(e.vatAmount) || 0), 0),
        sumWithholding: entries.reduce((s, e) => s + (Number(e.withholdingTaxAmount) || 0), 0),
    }), [entries]);

    const filteredEntries = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return entries;
        return entries.filter((row) => [
            row.sortCodeName,
            row.sortCodeId,
            row.description,
            row.reference,
            row.documentDate,
            row.reportingMonthDate,
            row.parsedEntityName,
            row.vatBusinessNumber,
            row.withholdingParty,
            String(row.amount ?? ''),
            String(row.withholdingTaxAmount ?? ''),
        ].some((val) => val && String(val).toLowerCase().includes(term)));
    }, [entries, search]);

    const pagedEntries = useMemo(
        () => filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredEntries, page, rowsPerPage]
    );

    const handleClose = () => {
        if (processing) return;
        onClose();
    };

    const handlePreview = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            resetPreview();
            const res = await previewAtzmaitCsv(file);
            if (!res) return;
            const missing = res.missingSortCodes ?? [];
            setEditableMissing(missing);
            setPreviewData({
                entries: res.entries ?? [],
                totalLines: res.parsedRows ?? 0,
                parseErrors: res.parseErrors ?? [],
                duplicateCount: res.duplicateCount ?? 0,
            });
            setTab(missing.length > 0 ? 'missing' : 'entries');
        } catch (err) {
            console.error('Preview error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!file || !previewData) return;
        if (missingIncomplete) {
            setTab('missing');
            return;
        }
        try {
            setProcessing(true);
            if (editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
            }
            const res = await commitAtzmaitCsv(file, importDuplicates);
            if (res === undefined) return;

            setMissingSortCodes([]);
            resetPreview();
            onFileReset();
            onClose();
        } catch (err) {
            console.error('Create & Commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing((prev) => prev.map((sc, i) => (i === index ? { ...sc, [field]: value } : sc)));

    const applyBulk = () => {
        setEditableMissing((prev) => prev.map((r) => (selectedRows.includes(r.sortCodeId)
            ? {
                ...r,
                sortCodeVatType: bulkVatType?.value ?? r.sortCodeVatType,
                sortCodeGroup: bulkGroup?.value ?? r.sortCodeGroup,
                participatesInProfitAndLoss: bulkPar?.value ?? r.participatesInProfitAndLoss,
                vat: bulkVat?.value ?? r.vat,
            }
            : r)));
        setSelectedRows([]);
        setBulkVatType(null);
        setBulkGroup(null);
        setBulkPar(null);
        setBulkVat(null);
    };

    const renderFile = () => (
        <div className={styles.section}>
            <DropzoneArea onDrop={onFileDrop} accept={CSV_ACCEPT} />
            {file && <p className={styles.fileName}>{t('FileSelected')}: {file.name}</p>}
            {previewData && (
                <div className={styles.stats}>
                    <XtramStat label={t('TotalLines')} value={previewData.totalLines} />
                    <XtramStat label={t('MissingSortCodes')} value={editableMissing.length} tone={editableMissing.length ? 'warning' : 'success'} />
                    {previewData.parseErrors.length > 0 && (
                        <XtramStat label={t('Errors')} value={previewData.parseErrors.length} tone="error" />
                    )}
                </div>
            )}
        </div>
    );

    const renderMissing = () => {
        if (editableMissing.length === 0) {
            return <XtramNotice type="success">✓ {t('NoMissingSortCodes')}</XtramNotice>;
        }
        const rowSelect = (sc, i, field, options, placeholder, value, required = true) => (
            <div className={styles.cellField}>
                <XtramSelect
                    direction={direction}
                    options={options}
                    placeholder={placeholder}
                    value={value}
                    onChange={(opt) => updateMissing(i, field, opt?.value ?? null)}
                    error={required && sc[field] == null}
                />
            </div>
        );

        return (
            <div className={styles.section}>
                <div className={styles.formFields}>
                    <XtramField>
                        <XtramSelect direction={direction} options={formattedSortCodeVatType} value={bulkVatType} onChange={setBulkVatType} placeholder={t('SelectSortCodeVatType')} />
                    </XtramField>
                    <XtramField>
                        <XtramSelect direction={direction} options={formattedSortCodesGroupNamesOptions} value={bulkGroup} onChange={setBulkGroup} placeholder={t('SelectSortCodeGroupName')} />
                    </XtramField>
                    <XtramField>
                        <XtramSelect direction={direction} options={participationOptions} value={bulkPar} onChange={setBulkPar} placeholder={t('participatesInProfitAndLoss')} />
                    </XtramField>
                    <XtramField size="narrow">
                        <XtramSelect direction={direction} options={vatPercentageOptions} value={bulkVat} onChange={setBulkVat} placeholder={t('VAT')} />
                    </XtramField>
                    <XtramButton
                        variant="ghost"
                        disabled={selectedRows.length === 0 || (!bulkVatType && !bulkGroup && !bulkPar && !bulkVat)}
                        onClick={applyBulk}
                    >
                        {t('ApplyToSelected')}
                    </XtramButton>
                </div>
                <XtramTable height="tall">
                    <Table size="small" stickyHeader dir={direction}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" className={styles.actionsCell}>
                                    <Checkbox
                                        size="small"
                                        className={styles.tableCheckbox}
                                        checked={selectedRows.length === editableMissing.length && editableMissing.length > 0}
                                        onChange={(e) => setSelectedRows(e.target.checked ? editableMissing.map((r) => r.sortCodeId) : [])}
                                    />
                                </TableCell>
                                <TableCell>{t('SortCode Id')}</TableCell>
                                <TableCell>{t('SortCode Name')}</TableCell>
                                <TableCell>{t('SelectSortCodeVatType')}</TableCell>
                                <TableCell>{t('SelectSortCodeGroupName')}</TableCell>
                                <TableCell>{t('participatesInProfitAndLoss')}</TableCell>
                                <TableCell>{t('VAT')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {editableMissing.map((sc, i) => (
                                <TableRow key={sc.sortCodeId}>
                                    <TableCell padding="checkbox" className={styles.actionsCell}>
                                        <Checkbox
                                            size="small"
                                            className={styles.tableCheckbox}
                                            checked={selectedRows.includes(sc.sortCodeId)}
                                            onChange={(e) => setSelectedRows((prev) => (e.target.checked
                                                ? [...prev, sc.sortCodeId]
                                                : prev.filter((id) => id !== sc.sortCodeId)))}
                                        />
                                    </TableCell>
                                    <TableCell>{sc.sortCodeId}</TableCell>
                                    <TableCell>
                                        <div className={styles.cellFieldWide}>
                                            <XtramInput
                                                direction={direction}
                                                placeholder={t('SortCode Name')}
                                                value={sc.sortCodeName ?? ''}
                                                onChange={(e) => updateMissing(i, 'sortCodeName', e.target.value)}
                                                error={!sc.sortCodeName?.trim()}
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {rowSelect(sc, i, 'sortCodeVatType', formattedSortCodeVatType, t('SelectSortCodeVatType'),
                                            formattedSortCodeVatType.find((opt) => opt.value === sc.sortCodeVatType?.toString()?.trim()) || null)}
                                    </TableCell>
                                    <TableCell>
                                        {rowSelect(sc, i, 'sortCodeGroup', formattedSortCodesGroupNamesOptions, t('SelectSortCodeGroupName'),
                                            formattedSortCodesGroupNamesOptions.find((opt) => opt.value === sc.sortCodeGroup) || null, false)}
                                    </TableCell>
                                    <TableCell>
                                        {rowSelect(sc, i, 'participatesInProfitAndLoss', participationOptions, t('participatesInProfitAndLoss'),
                                            participationOptions.find((opt) => opt.value === sc.participatesInProfitAndLoss) || null)}
                                    </TableCell>
                                    <TableCell>
                                        <div className={styles.cellFieldNarrow}>
                                            <XtramSelect
                                                direction={direction}
                                                options={vatPercentageOptions}
                                                placeholder={t('VAT')}
                                                value={vatPercentageOptions.find((opt) => opt.value === sc.vat) || null}
                                                onChange={(opt) => updateMissing(i, 'vat', opt?.value ?? null)}
                                                error={sc.vat == null}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </XtramTable>
            </div>
        );
    };

    const renderEntries = () => (
        <>
            <div className={styles.section}>
                <div className={styles.stats}>
                    <XtramStat label={t('TotalLines')} value={previewData.totalLines} />
                    <XtramStat label={t('SumAmount')} value={formatAmount(summaries.sumAmount)} />
                    <XtramStat label={t('SumNetAmount')} value={formatAmount(summaries.sumNet)} />
                    <XtramStat label={t('SumVatAmount')} value={formatAmount(summaries.sumVat)} />
                    <XtramStat label={t('WithholdingTax')} value={formatAmount(summaries.sumWithholding)} />
                    {previewData.parseErrors.length > 0 && (
                        <XtramStat label={t('Errors')} value={previewData.parseErrors.length} tone="error" />
                    )}
                </div>

                {previewData.duplicateCount > 0 && (
                    <XtramNotice type="warning">
                        <span>⚠ {t('DuplicatesFoundInDb')}: <strong>{previewData.duplicateCount}</strong></span>
                        <XtramCheckbox checked={importDuplicates} onChange={setImportDuplicates} label={t('ImportDuplicatesAnyway')} />
                    </XtramNotice>
                )}

                {previewData.parseErrors.length > 0 && (
                    <XtramNotice type="warning">
                        <strong>{t('Errors')} ({previewData.parseErrors.length})</strong>
                        <ul className={styles.noticeList}>
                            {previewData.parseErrors.map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                    </XtramNotice>
                )}
            </div>

            <div className={styles.section}>
                <div className={styles.toolbar}>
                    <h3 className={styles.sectionTitle}>
                        {t('JournalEntries')} ({filteredEntries.length}{search ? ` / ${entries.length}` : ''})
                    </h3>
                    <div className={styles.formFieldWide}>
                        <XtramSearch
                            direction={direction}
                            value={search}
                            onChange={(v) => { setSearch(v); setPage(0); }}
                            placeholder={t('Search')}
                        />
                    </div>
                </div>

                {previewData.totalLines === 0 ? (
                    <div className={styles.emptyState}>{t('importModal.noRowsParsed')}</div>
                ) : (
                    <XtramTable height="tall">
                        <Table size="small" stickyHeader dir={direction}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Line')}</TableCell>
                                    <TableCell>{t('DocumentDate')}</TableCell>
                                    <TableCell>{t('ReportingMonth')}</TableCell>
                                    <TableCell>{t('SortCode Id')}</TableCell>
                                    <TableCell>{t('SortCode Name')}</TableCell>
                                    <TableCell className={styles.numCell}>{t('Amount')}</TableCell>
                                    <TableCell className={styles.numCell}>{t('SumNetAmount')}</TableCell>
                                    <TableCell className={styles.numCell}>{t('VAT')}</TableCell>
                                    <TableCell className={styles.numCell}>{t('WithholdingTax')}</TableCell>
                                    <TableCell>{t('WithholdingParty')}</TableCell>
                                    <TableCell>{t('Reference')}</TableCell>
                                    <TableCell>{t('BusinessName')}</TableCell>
                                    <TableCell>{t('Description')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedEntries.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={13} className={styles.emptyState}>{t('importModal.noResults')}</TableCell>
                                    </TableRow>
                                ) : pagedEntries.map((row, i) => (
                                    <TableRow
                                        key={`${row.lineNumber}-${row.documentDate}-${i}`}
                                        className={row.isDuplicate ? styles.rowWarning : undefined}
                                    >
                                        <TableCell>
                                            {row.lineNumber}
                                            {row.isDuplicate && (
                                                <Tooltip title={t('DuplicateInDb')}>
                                                    <span className={styles.duplicateMark}>⚠</span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>{row.documentDate ?? '—'}</TableCell>
                                        <TableCell>{row.reportingMonthDate ?? '—'}</TableCell>
                                        <TableCell>{row.sortCodeId ?? '—'}</TableCell>
                                        <TableCell className={styles.wrapCell}>{row.sortCodeName ?? ''}</TableCell>
                                        <TableCell className={styles.numCell}>{formatAmount(row.amount)}</TableCell>
                                        <TableCell className={styles.numCell}>{formatAmount(row.originalAmount)}</TableCell>
                                        <TableCell className={styles.numCell}>{formatAmount(row.vatAmount)}</TableCell>
                                        <TableCell className={`${styles.numCell} ${Number(row.withholdingTaxAmount) > 0 ? styles.cellWarning : ''}`}>
                                            {formatAmount(row.withholdingTaxAmount)}
                                        </TableCell>
                                        <TableCell>
                                            {row.withholdingParty === 'supplier'
                                                ? t('Supplier')
                                                : row.withholdingParty === 'client'
                                                    ? t('Client')
                                                    : '—'}
                                        </TableCell>
                                        <TableCell title={row.reference}>{row.reference ?? '—'}</TableCell>
                                        <TableCell className={styles.wrapCell} title={row.parsedEntityName}>{row.parsedEntityName ?? '—'}</TableCell>
                                        <TableCell className={styles.wrapCell} title={row.description}>{row.description ?? ''}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={filteredEntries.length}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage={t('RowsPerPage')}
                        />
                    </XtramTable>
                )}
            </div>
        </>
    );

    const tabs = [
        { key: 'file', label: t('importModal.fileTab') },
        {
            key: 'missing',
            label: t('MissingSortCodes'),
            count: previewData ? editableMissing.length : null,
            alert: missingIncomplete,
            disabled: !previewData,
        },
        {
            key: 'entries',
            label: t('JournalEntries'),
            count: previewData ? entries.length : null,
            disabled: !previewData,
        },
    ];

    return (
        <XtramModal
            open={open}
            onClose={handleClose}
            direction={direction}
            busy={processing}
            expanded={!!previewData}
            title={t('UploadAtzmaitCsv')}
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={handleClose} disabled={processing}>
                        {t('cancelButton')}
                    </XtramButton>
                    <XtramButton variant="ghost" onClick={handlePreview} disabled={!file || processing}>
                        {t('Preview')}
                    </XtramButton>
                    <XtramButton
                        variant="primary"
                        onClick={handleCreateAndCommit}
                        disabled={!previewData || processing}
                        busy={processing}
                    >
                        {t('CreateAndImport')}
                    </XtramButton>
                </>
            )}
        >
            {tab === 'file' && renderFile()}
            {tab === 'missing' && previewData && renderMissing()}
            {tab === 'entries' && previewData && renderEntries()}
        </XtramModal>
    );
};

export default AtzmaitCsvImportModal;
